import React from 'react';
import { useNavigate } from 'react-router-dom';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { alpha } from '@mui/material/styles';

import { ThemeContext } from 'src/store/ContextStore';
import { useAutoTranslation } from 'src/hooks/useTranslate';

// Independent header card for the clinician "view form response" page. Sits
// directly above the form (submission) panel and carries: back navigation, the
// response id as a heading, who submitted + when, and the PDF actions. Width
// mirrors the form card (previewContainer: max-width 1200, centred) so the two
// stack cleanly. The Back button follows the group's appearance settings — an
// outlined white button whose border + text use the configured button colour.
export default function ResponseHeaderPanel({
  backLink,
  responseId,
  user,
  submittedAt,
  onGeneratePdf,
  onViewVcgs,
  showVcgs,
  vcgsLoading
}) {
  const navigate = useNavigate();
  const t = useAutoTranslation();
  const { theme } = React.useContext(ThemeContext);
  const accent = theme?.buttonStyle || '#1a3a5c';

  const submitterName = [user?.firstName, user?.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  const submittedDate = submittedAt
    ? new Date(submittedAt).toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short'
      })
    : null;

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        maxWidth: 1200,
        mx: 'auto',
        mb: 2,
        p: 3,
        border: '1px solid var(--color-border)',
        borderRadius: '14px',
        bgcolor: '#fff'
      }}
    >
      <Button
        variant="outlined"
        size="small"
        startIcon={<ChevronLeftIcon />}
        onClick={() => navigate(backLink)}
        sx={{
          mb: 2,
          color: accent,
          borderColor: accent,
          bgcolor: '#fff',
          textTransform: 'none',
          '&:hover': { borderColor: accent, bgcolor: alpha(accent, 0.06) }
        }}
      >
        {t('Back')}
      </Button>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}
      >
        <Box>
          <Typography
            variant="h5"
            component="h1"
            sx={{ fontWeight: 700, color: 'var(--color-navy)' }}
          >
            {t('Response')} #{responseId}
          </Typography>
          <Stack spacing={0.25} sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              <Box component="span" sx={{ fontWeight: 600 }}>
                {t('Submitted by')}:
              </Box>{' '}
              {submitterName ? `${submitterName} · ` : ''}
              {user?.email || '—'}
            </Typography>
            {submittedDate && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                <Box component="span" sx={{ fontWeight: 600 }}>
                  {t('Submitted')}:
                </Box>{' '}
                {submittedDate}
              </Typography>
            )}
          </Stack>
        </Box>

        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<PictureAsPdfIcon />}
            onClick={onGeneratePdf}
          >
            {t('Generate PDF')}
          </Button>
          {showVcgs && (
            <Button
              variant="contained"
              color="secondary"
              disabled={vcgsLoading}
              startIcon={
                vcgsLoading ? <CircularProgress size={16} /> : undefined
              }
              onClick={onViewVcgs}
            >
              {t('View VCGS PDF')}
            </Button>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}
